/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { api } from "../../lib/auth";
import { toast } from "sonner";
import {
  Trophy, Users, ListChecks, Settings as SettingsIcon, Plus, Loader2, X, Search, Copy,
  Banknote, BarChart3, CheckCircle2, Bell, Crown, Calendar, Shield, MailWarning,
  Pencil, Ban, Trash2,
} from "lucide-react";

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

const TABS = [
  { k: "dashboard", lbl: "Dashboard", icon: BarChart3 },
  { k: "members", lbl: "Members", icon: Users },
  { k: "dues", lbl: "Dues & Levies", icon: ListChecks },
  { k: "payments", lbl: "Payments", icon: Banknote },
  { k: "loans", lbl: "Loans", icon: Banknote },
  { k: "teams", lbl: "Teams & Draft", icon: Shield },
  { k: "fixtures", lbl: "Fixtures", icon: Calendar },
  { k: "executives", lbl: "Executives", icon: Crown },
  { k: "events", lbl: "Events", icon: Calendar },
  { k: "reminders", lbl: "Reminders", icon: MailWarning },
  { k: "settings", lbl: "Club Settings", icon: SettingsIcon },
];

export default function FootballClubPage() {
  const [tab, setTab] = useState("dashboard");
  return (
    <div className="p-6 lg:p-8" data-testid="football-club-page">
      <header className="mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center"><Trophy size={22} /></div>
          <div>
            <h1 className="font-display font-extrabold text-3xl text-black">Football Club</h1>
            <p className="text-sm text-black/55">Manage your club members, dues, draft & competitions.</p>
          </div>
        </div>
      </header>
      <div className="flex items-center gap-1 mb-6 border-b border-black/8 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon, active = tab === t.k;
          return (
            <button key={t.k} onClick={() => setTab(t.k)} data-testid={`fc-tab-${t.k}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-bold border-b-2 whitespace-nowrap ${active ? "border-emerald-600 text-emerald-700" : "border-transparent text-black/55 hover:text-black"}`}>
              <Icon size={14} /> {t.lbl}
            </button>
          );
        })}
      </div>
      {tab === "dashboard" && <Dashboard />}
      {tab === "members" && <Members />}
      {tab === "dues" && <Dues />}
      {tab === "payments" && <Payments />}
      {tab === "loans" && <Loans />}
      {tab === "teams" && <Teams />}
      {tab === "fixtures" && <Fixtures />}
      {tab === "executives" && <Executives />}
      {tab === "events" && <Events />}
      {tab === "reminders" && <Reminders />}
      {tab === "settings" && <Settings />}
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loans, setLoans] = useState([]);
  const subdomain = window.location.pathname.split("/")[2] || "";
  const clubUrl = `${window.location.origin}/club`;
  useEffect(() => {
    api.get("/t/club/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/t/club/members").then(async () => {
      // recent payments across members
      const m = await api.get("/t/club/members");
      const list = m.data || [];
      const recent = [];
      // Aggregate via single fetch (limit 5 members for perf)
      for (const member of list.slice(0, 25)) {
        try {
          const r = await api.get(`/t/club/members/${member.id}`);
          (r.data.payments || []).slice(0, 3).forEach((p) => recent.push({ ...p, member_name: member.full_name }));
        } catch { /* silent */ }
      }
      recent.sort((a, b) => (b.paid_at || "").localeCompare(a.paid_at || ""));
      setPayments(recent.slice(0, 10));
    }).catch(() => {});
    api.get("/t/club/loans?status=pending").then((r) => setLoans(r.data || [])).catch(() => {});
  }, []);
  if (!d) return <Spinner />;
  return (
    <div data-testid="fc-dashboard" className="space-y-6">
      {/* Club code & member portal */}
      <div className="bg-gradient-to-br from-emerald-600 via-green-700 to-emerald-900 text-white rounded-3xl p-6 lg:p-8 relative overflow-hidden">
        <div className="absolute -top-24 -right-24 w-80 h-80 rounded-full bg-emerald-400/20 blur-3xl" />
        <div className="relative grid md:grid-cols-3 gap-5">
          <div className="md:col-span-2">
            <div className="text-xs uppercase font-bold tracking-[0.18em] text-white/75">Member sign-in portal</div>
            <h2 className="font-display font-extrabold text-2xl mt-1">Share with your members</h2>
            <p className="text-sm text-white/75 mt-1">Members sign in at the portal below using the club code and the credentials you provided.</p>
            <div className="mt-4 space-y-2">
              {/* <div className="bg-white/15 backdrop-blur border border-white/20 rounded-xl p-3 flex items-center gap-3">
                <div className="text-[10px] uppercase font-bold tracking-wider opacity-80 w-24">Portal URL</div>
                <code className="flex-1 font-mono text-sm truncate">{clubUrl}</code>
                <button onClick={() => { navigator.clipboard.writeText(clubUrl); toast.success("Copied"); }} data-testid="fc-copy-url" className="p-1.5 rounded hover:bg-white/15"><Copy size={13} /></button>
              </div> */}
              <div className="bg-white/15 backdrop-blur border border-white/20 rounded-xl p-3 flex items-center gap-3">
                <div className="text-[10px] uppercase font-bold tracking-wider opacity-80 w-24">Club code</div>
                <code className="flex-1 font-mono text-lg font-extrabold tracking-widest">{subdomain.toUpperCase()}</code>
                <button onClick={() => { navigator.clipboard.writeText(subdomain); toast.success("Copied"); }} data-testid="fc-copy-code" className="p-1.5 rounded hover:bg-white/15"><Copy size={13} /></button>
              </div>
            </div>
          </div>
          <div className="bg-white/15 backdrop-blur border border-white/20 rounded-2xl p-4">
            <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">QR / Quick share</div>
            <div className="mt-2 font-mono text-[10px] break-all opacity-85">{`${clubUrl}/login?club=${subdomain}`}</div>
            <button onClick={() => { navigator.clipboard.writeText(`${clubUrl}/login?club=${subdomain}`); toast.success("Share link copied"); }} className="mt-3 w-full py-2 rounded-lg bg-white text-emerald-900 text-xs font-bold">Copy share link</button>
          </div>
        </div>
      </div>

      {/* KPI band */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPI title="Total members" value={d.total_members} icon={Users} accent="bg-blue-50 text-blue-700" />
        <KPI title="Active members" value={d.active_members} icon={CheckCircle2} accent="bg-emerald-50 text-emerald-700" />
        <KPI title="Collected this month" value={ngn(d.dues_collected_month)} icon={Banknote} accent="bg-violet-50 text-violet-700" />
        <KPI title="Outstanding dues" value={ngn(d.outstanding_dues_total)} icon={ListChecks} accent="bg-amber-50 text-amber-700" />
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
            <div className="font-display font-extrabold text-base">Recent payments</div>
            <Banknote className="text-emerald-600" size={16} />
          </div>
          {payments.length === 0 ? <Empty msg="No payments yet" /> : (
            <div className="divide-y divide-black/5">
              {payments.map((p) => (
                <div key={p.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`fc-dash-pay-${p.id}`}>
                  <div>
                    <div className="font-bold">{p.member_name}</div>
                    <div className="text-xs text-black/55 capitalize">{(p.kind || "").replace("_", " ")} · {(p.paid_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="font-bold text-emerald-700">{ngn(p.amount)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
            <div className="font-display font-extrabold text-base">Pending loan requests</div>
            <Banknote className="text-amber-600" size={16} />
          </div>
          {loans.length === 0 ? <Empty msg="No pending loans" /> : (
            <div className="divide-y divide-black/5">
              {loans.slice(0, 8).map((l) => (
                <div key={l.id} className="px-5 py-3 flex items-center justify-between text-sm" data-testid={`fc-dash-loan-${l.id}`}>
                  <div>
                    <div className="font-bold">{l.member_name}</div>
                    <div className="text-xs text-black/55 truncate max-w-xs">{l.purpose}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">{ngn(l.amount)}</div>
                    <div className="text-[10px] text-amber-700 uppercase font-bold">{l.term_months}m term</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Analytics — simple bar of dues vs collected */}
      <div className="bg-white border border-black/8 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="font-display font-extrabold text-base">This month at a glance</div>
          <BarChart3 size={16} className="text-black/40" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <div className="text-xs text-black/55">Collected</div>
            <div className="font-display font-extrabold text-3xl text-emerald-700 mt-1">{ngn(d.dues_collected_month)}</div>
            <div className="mt-3 h-3 bg-black/5 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: d.outstanding_dues_total > 0 ? `${Math.min(100, (d.dues_collected_month / (d.dues_collected_month + d.outstanding_dues_total)) * 100)}%` : "100%" }} />
            </div>
          </div>
          <div>
            <div className="text-xs text-black/55">Outstanding ({d.outstanding_dues_count} dues)</div>
            <div className="font-display font-extrabold text-3xl text-amber-700 mt-1">{ngn(d.outstanding_dues_total)}</div>
            <div className="mt-3 h-3 bg-black/5 rounded-full overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: (d.dues_collected_month + d.outstanding_dues_total) > 0 ? `${Math.min(100, (d.outstanding_dues_total / (d.dues_collected_month + d.outstanding_dues_total)) * 100)}%` : "0%" }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Members() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [show, setShow] = useState(false);
  const [selected, setSelected] = useState(null);
  const load = async () => { setItems((await api.get("/t/club/members")).data || []); };
  useEffect(() => { load(); }, []);
  const filtered = items.filter((m) => !q || (m.full_name || "").toLowerCase().includes(q.toLowerCase()) || (m.email || "").includes(q.toLowerCase()));
  return (
    <div data-testid="fc-members">
      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-black/40" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…"
            className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-black/15 text-sm" data-testid="fc-members-search" />
        </div>
        <button onClick={() => setShow(true)} data-testid="fc-member-new-btn"
          className="px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold inline-flex items-center gap-2">
          <Plus size={14} /> New Member
        </button>
      </div>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-12 text-center"><Users size={28} className="mx-auto text-black/25 mb-2" /><div className="font-bold text-black/55">No members yet</div></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Member</Th><Th>Membership #</Th><Th>Phone</Th><Th>Status</Th><Th>Joined</Th><Th /></tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.id} className="border-t border-black/5 hover:bg-black/[0.02] cursor-pointer" onClick={() => setSelected(m.id)} data-testid={`fc-member-row-${m.id}`}>
                  <Td>
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center font-bold">{(m.full_name || "?")[0]}</div>
                      <div>
                        <div className="font-bold">{m.full_name}</div>
                        <div className="text-xs text-black/55">{m.email}</div>
                      </div>
                    </div>
                  </Td>
                  <Td><code className="font-mono text-xs bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{m.membership_no}</code></Td>
                  <Td>{m.phone}</Td>
                  <Td><span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${m.status === "active" ? "bg-emerald-100 text-emerald-700" : m.status === "pending_payment" ? "bg-amber-100 text-amber-700" : m.status === "blocked" ? "bg-rose-100 text-rose-700" : m.status === "terminated" ? "bg-zinc-200 text-zinc-700" : "bg-zinc-100 text-zinc-700"}`}>{(m.status || "").replace("_", " ")}</span></Td>
                  <Td className="text-xs text-black/55">{(m.joined_at || "").slice(0, 10)}</Td>
                  <Td />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {show && <MemberEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {selected && <MemberDetail mid={selected} onClose={() => setSelected(null)} reload={load} />}
    </div>
  );
}

function MemberEditor({ onClose, onSaved }) {
  const [f, setF] = useState({ full_name: "", phone: "", email: "", gender: "Male", date_of_birth: "", state: "", lga: "", occupation: "", address: "", emergency_contact: "", photo_url: "", playing_position: "", form: "", jersey_no: "", is_paid: false });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/t/products/upload", fd);
      setF((s) => ({ ...s, photo_url: r.data.url }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const submit = async () => {
    if (!f.full_name || !f.phone || !f.email) return toast.error("Name, phone & email required");
    setSaving(true);
    try {
      const payload = { ...f, jersey_no: f.jersey_no ? parseInt(f.jersey_no) : null };
      const r = await api.post("/t/club/members", payload);
      setResult(r.data);
      toast.success(f.is_paid ? "Member activated — share login credentials below" : "Member created (pending payment) — share login credentials below");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  if (result) {
    return (
      <Modal title="Member created" onClose={onClose}>
        <div className={`${result.member.status === "active" ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-amber-50 border-amber-200 text-amber-900"} border rounded-xl p-5 text-center`}>
          <CheckCircle2 size={28} className={`mx-auto mb-2 ${result.member.status === "active" ? "text-emerald-600" : "text-amber-600"}`} />
          <div className="font-display font-extrabold text-lg">{result.member.full_name}</div>
          <div className="text-xs mt-1">Membership #: <code className="font-mono bg-white px-2 py-0.5 rounded">{result.member.membership_no}</code></div>
          <div className="mt-1.5 text-[10px] uppercase font-bold tracking-wider">Status: {(result.member.status || "").replace("_", " ")}</div>
          <div className="mt-4 text-left">
            <div className="text-[11px] uppercase font-bold text-black/55 mb-1">Initial password (share securely)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-sm bg-white px-3 py-2 rounded-lg border">{result.initial_password}</code>
              <button onClick={() => { navigator.clipboard.writeText(result.initial_password); toast.success("Copied"); }} className="px-3 py-2 rounded-lg bg-emerald-600 text-white"><Copy size={13} /></button>
            </div>
            {result.invoice && (
              <div className="mt-4 text-xs">
                Registration invoice: <strong>{result.invoice.title}</strong> · {ngn(result.invoice.amount)} <br />
                {result.member.status === "active"
                  ? "Already marked as paid — member is active and can sign in immediately."
                  : "Member will be activated automatically once the registration fee is recorded."}
              </div>
            )}
          </div>
        </div>
        <button onClick={() => { onClose(); onSaved(); }} className="w-full mt-5 py-3 rounded-lg bg-black text-white font-bold text-sm">Done</button>
      </Modal>
    );
  }
  return (
    <Modal title="New club member" onClose={onClose}>
      {/* Photo + identity */}
      <div className="mb-4 flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center overflow-hidden flex-shrink-0">
          {f.photo_url ? (
            <img src={f.photo_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="font-display font-extrabold text-2xl">{(f.full_name || "?")[0]}</span>
          )}
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase font-bold text-black/55 mb-1.5">Profile photo</div>
          <label className="inline-block px-3 py-2 rounded-lg bg-black text-white text-xs font-bold cursor-pointer hover:bg-black/85" data-testid="fc-m-photo-upload">
            {uploading ? "Uploading…" : f.photo_url ? "Change photo" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </label>
          {f.photo_url && (
            <button onClick={() => setF({ ...f, photo_url: "" })} className="ml-2 text-xs font-bold text-rose-600">Remove</button>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Full name *" full><Input value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="fc-m-name" /></Field>
        <Field lbl="Phone *"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="fc-m-phone" /></Field>
        <Field lbl="Email *"><Input type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="fc-m-email" /></Field>
        <Field lbl="Gender"><select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm"><option>Male</option><option>Female</option><option>Other</option></select></Field>
        <Field lbl="Date of birth"><Input type="date" value={f.date_of_birth} onChange={(v) => setF({ ...f, date_of_birth: v })} /></Field>
        <Field lbl="State"><Input value={f.state} onChange={(v) => setF({ ...f, state: v })} /></Field>
        <Field lbl="LGA"><Input value={f.lga} onChange={(v) => setF({ ...f, lga: v })} /></Field>
        <Field lbl="Occupation"><Input value={f.occupation} onChange={(v) => setF({ ...f, occupation: v })} /></Field>
        <Field lbl="Emergency contact"><Input value={f.emergency_contact} onChange={(v) => setF({ ...f, emergency_contact: v })} /></Field>
        <Field lbl="Address" full><textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} rows={2} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      {/* Football-specific profile */}
      <div className="mt-4 bg-[#FAFBFD] border border-black/8 rounded-xl p-4">
        <div className="text-[11px] uppercase font-bold text-emerald-700 tracking-wider mb-3">Football profile</div>
        <div className="grid grid-cols-3 gap-3">
          <Field lbl="Playing position">
            <select value={f.playing_position} onChange={(e) => setF({ ...f, playing_position: e.target.value })}
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-m-position">
              <option value="">—</option>
              <option value="GK">Goalkeeper (GK)</option>
              <option value="CB">Centre Back (CB)</option>
              <option value="LB">Left Back (LB)</option>
              <option value="RB">Right Back (RB)</option>
              <option value="CDM">Defensive Mid (CDM)</option>
              <option value="CM">Centre Mid (CM)</option>
              <option value="CAM">Attacking Mid (CAM)</option>
              <option value="LW">Left Wing (LW)</option>
              <option value="RW">Right Wing (RW)</option>
              <option value="ST">Striker (ST)</option>
              <option value="CF">Centre Forward (CF)</option>
            </select>
          </Field>
          <Field lbl="Current form">
            <select value={f.form} onChange={(e) => setF({ ...f, form: e.target.value })}
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-m-form">
              <option value="">—</option>
              <option value="Excellent">🔥 Excellent</option>
              <option value="Good">✅ Good</option>
              <option value="Fair">➖ Fair</option>
              <option value="Poor">⚠️ Poor</option>
              <option value="Recovering">🩹 Recovering</option>
            </select>
          </Field>
          <Field lbl="Jersey #">
            <Input type="number" value={f.jersey_no} onChange={(v) => setF({ ...f, jersey_no: v })} testid="fc-m-jersey" />
          </Field>
        </div>
      </div>
      <div className={`mt-4 rounded-xl border p-3 ${f.is_paid ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}>
        <label className="flex items-start gap-3 cursor-pointer">
          <input type="checkbox" checked={!!f.is_paid} onChange={(e) => setF({ ...f, is_paid: e.target.checked })}
            className="mt-1 accent-emerald-600" data-testid="fc-m-paid" />
          <div className="flex-1">
            <div className="font-bold text-sm text-black">Registration fee already paid?</div>
            <div className="text-xs text-black/65 mt-0.5">
              {f.is_paid
                ? "Member will be activated immediately and the registration invoice marked as paid."
                : "Member will be created in pending status and can pay later from their portal."}
            </div>
          </div>
        </label>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={saving} testid="fc-m-save" />
    </Modal>
  );
}

function MemberDetail({ mid, onClose, reload }) {
  const [data, setData] = useState(null);
  const [paying, setPaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [acting, setActing] = useState("");
  const load = () => api.get(`/t/club/members/${mid}`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [mid]);
  if (!data) return null;
  const { member, dues, payments, stats } = data;

  const recordPayment = async (kind, amount) => {
    setPaying(true);
    try {
      await api.post("/t/club/payments", { member_id: member.id, amount: parseFloat(amount), kind, method: "cash" });
      toast.success("Payment recorded");
      load(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setPaying(false); }
  };

  const doBlock = async () => {
    const reason = window.prompt("Reason for blocking this member? (e.g. unpaid dues > 3 months)", "");
    if (reason === null) return;
    setActing("block");
    try {
      await api.post(`/t/club/members/${member.id}/block`, { reason });
      toast.success("Member blocked");
      load(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActing(""); }
  };
  const doUnblock = async () => {
    if (!window.confirm(`Restore ${member.full_name} to active status?`)) return;
    setActing("unblock");
    try {
      await api.post(`/t/club/members/${member.id}/unblock`);
      toast.success("Member reactivated");
      load(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActing(""); }
  };
  const doTerminate = async () => {
    const reason = window.prompt(
      `Terminate ${member.full_name}? This stops them from logging in. They can be restored later via "Unblock". Type a reason:`, ""
    );
    if (reason === null) return;
    setActing("terminate");
    try {
      await api.post(`/t/club/members/${member.id}/terminate`, { reason });
      toast.success("Member terminated");
      load(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setActing(""); }
  };

  const statusPill = member.status === "active" ? "bg-emerald-100 text-emerald-700"
    : member.status === "blocked" ? "bg-rose-100 text-rose-700"
    : member.status === "terminated" ? "bg-zinc-200 text-zinc-700"
    : "bg-amber-100 text-amber-700";

  return (
    <Modal title={member.full_name} onClose={onClose} wide>
      <div className="flex items-start gap-4 mb-5 pb-4 border-b border-black/8">
        <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center overflow-hidden flex-shrink-0">
          {member.photo_url ? <img src={member.photo_url} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-extrabold text-xl">{(member.full_name || "?")[0]}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded inline-block">{member.membership_no}</div>
          <div className="mt-1 flex items-center gap-1.5 flex-wrap text-xs">
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${statusPill}`} data-testid="fc-md-status">
              {(member.status || "").replace("_", " ")}
            </span>
            {member.playing_position && <span className="px-2 py-0.5 rounded bg-black text-white text-[10px] font-bold uppercase">{member.playing_position}</span>}
            {member.jersey_no != null && member.jersey_no !== "" && <span className="px-2 py-0.5 rounded bg-emerald-600 text-white text-[10px] font-bold">#{member.jersey_no}</span>}
            {member.form && <span className="px-2 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800 text-[10px] font-bold">{member.form}</span>}
          </div>
          <div className="text-xs text-black/55 mt-1.5">{member.email} · {member.phone}</div>
          {member.status === "blocked" && member.blocked_reason && (
            <div className="mt-2 text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">Blocked: {member.blocked_reason}</div>
          )}
          {member.status === "terminated" && member.terminated_reason && (
            <div className="mt-2 text-[11px] text-zinc-700 bg-zinc-100 border border-zinc-200 rounded px-2 py-1.5">Terminated: {member.terminated_reason}</div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 flex-shrink-0">
          <button onClick={() => setEditing(true)} data-testid="fc-md-edit"
            className="px-3 py-1.5 text-xs font-bold rounded-lg bg-black text-white hover:bg-black/85 inline-flex items-center gap-1.5">
            <Pencil size={12} /> Edit
          </button>
          {member.status === "active" && (
            <button onClick={doBlock} disabled={!!acting} data-testid="fc-md-block"
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-amber-100 hover:bg-amber-200 text-amber-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Ban size={12} /> {acting === "block" ? "Blocking…" : "Block"}
            </button>
          )}
          {(member.status === "blocked" || member.status === "terminated") && (
            <button onClick={doUnblock} disabled={!!acting} data-testid="fc-md-unblock"
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              <CheckCircle2 size={12} /> {acting === "unblock" ? "Restoring…" : "Reactivate"}
            </button>
          )}
          {member.status !== "terminated" && (
            <button onClick={doTerminate} disabled={!!acting} data-testid="fc-md-terminate"
              className="px-3 py-1.5 text-xs font-bold rounded-lg bg-rose-100 hover:bg-rose-200 text-rose-800 disabled:opacity-50 inline-flex items-center gap-1.5">
              <Trash2 size={12} /> {acting === "terminate" ? "Terminating…" : "Terminate"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <KPI title="Outstanding" value={ngn(stats.outstanding)} accent="bg-rose-50 text-rose-700" />
        <KPI title="Total paid" value={ngn(stats.total_paid)} accent="bg-emerald-50 text-emerald-700" />
        <KPI title="Member since" value={(member.joined_at || "").slice(0, 10)} accent="bg-blue-50 text-blue-700" />
      </div>

      <h3 className="font-display font-extrabold text-base mb-2">Pending dues</h3>
      <div className="bg-[#FAFBFD] rounded-xl p-3 mb-5">
        {dues.filter((d) => d.status === "pending").length === 0 ? (
          <div className="text-xs text-black/45 text-center py-3">No pending dues</div>
        ) : (
          dues.filter((d) => d.status === "pending").map((d) => (
            <div key={d.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
              <div>
                <div className="font-bold text-sm">{d.title}</div>
                <div className="text-xs text-black/55 capitalize">{(d.kind || "").replace("_", " ")}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-rose-700">{ngn(d.amount)}</span>
                <button onClick={() => recordPayment(d.kind, d.amount)} disabled={paying} data-testid={`fc-due-record-${d.id}`}
                  className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded">Mark paid</button>
              </div>
            </div>
          ))
        )}
      </div>

      <h3 className="font-display font-extrabold text-base mb-2">Recent payments</h3>
      <div className="bg-[#FAFBFD] rounded-xl p-3">
        {payments.length === 0 ? (
          <div className="text-xs text-black/45 text-center py-3">No payments</div>
        ) : payments.slice(0, 10).map((p) => (
          <div key={p.id} className="flex items-center justify-between py-2 border-b border-black/5 last:border-0">
            <div>
              <div className="font-bold text-sm capitalize">{(p.kind || "").replace("_", " ")}</div>
              <div className="text-xs text-black/55">{(p.paid_at || "").slice(0, 16).replace("T", " ")} · {p.method}</div>
            </div>
            <div className="font-bold text-emerald-700">{ngn(p.amount)}</div>
          </div>
        ))}
      </div>

      {editing && (
        <MemberEditModal member={member}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); reload(); }} />
      )}
    </Modal>
  );
}

function MemberEditModal({ member, onClose, onSaved }) {
  const [f, setF] = useState({
    full_name: member.full_name || "", phone: member.phone || "", email: member.email || "",
    gender: member.gender || "Male", date_of_birth: member.date_of_birth || "",
    state: member.state || "", lga: member.lga || "", occupation: member.occupation || "",
    address: member.address || "", emergency_contact: member.emergency_contact || "",
    photo_url: member.photo_url || "",
    playing_position: member.playing_position || "", form: member.form || "",
    jersey_no: member.jersey_no ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/t/products/upload", fd);
      setF((s) => ({ ...s, photo_url: r.data.url }));
      toast.success("Photo uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, jersey_no: f.jersey_no === "" ? null : parseInt(f.jersey_no) };
      await api.patch(`/t/club/members/${member.id}`, payload);
      toast.success("Member updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title="Edit member" onClose={onClose}>
      <div className="mb-4 flex items-center gap-4">
        <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center overflow-hidden flex-shrink-0">
          {f.photo_url ? <img src={f.photo_url} alt="" className="w-full h-full object-cover" /> : <span className="font-display font-extrabold text-2xl">{(f.full_name || "?")[0]}</span>}
        </div>
        <div className="flex-1">
          <div className="text-[11px] uppercase font-bold text-black/55 mb-1.5">Profile photo</div>
          <label className="inline-block px-3 py-2 rounded-lg bg-black text-white text-xs font-bold cursor-pointer hover:bg-black/85" data-testid="fc-me-photo-upload">
            {uploading ? "Uploading…" : f.photo_url ? "Change photo" : "Upload photo"}
            <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} />
          </label>
          {f.photo_url && <button onClick={() => setF({ ...f, photo_url: "" })} className="ml-2 text-xs font-bold text-rose-600">Remove</button>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field lbl="Full name" full><Input value={f.full_name} onChange={(v) => setF({ ...f, full_name: v })} testid="fc-me-name" /></Field>
        <Field lbl="Phone"><Input value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testid="fc-me-phone" /></Field>
        <Field lbl="Email"><Input type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} /></Field>
        <Field lbl="Gender">
          <select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
            <option>Male</option><option>Female</option><option>Other</option>
          </select>
        </Field>
        <Field lbl="Date of birth"><Input type="date" value={f.date_of_birth} onChange={(v) => setF({ ...f, date_of_birth: v })} /></Field>
        <Field lbl="State"><Input value={f.state} onChange={(v) => setF({ ...f, state: v })} /></Field>
        <Field lbl="LGA"><Input value={f.lga} onChange={(v) => setF({ ...f, lga: v })} /></Field>
        <Field lbl="Occupation"><Input value={f.occupation} onChange={(v) => setF({ ...f, occupation: v })} /></Field>
        <Field lbl="Emergency contact"><Input value={f.emergency_contact} onChange={(v) => setF({ ...f, emergency_contact: v })} /></Field>
        <Field lbl="Address" full>
          <textarea value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} rows={2}
            className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
        </Field>
      </div>
      <div className="mt-4 bg-[#FAFBFD] border border-black/8 rounded-xl p-4">
        <div className="text-[11px] uppercase font-bold text-emerald-700 tracking-wider mb-3">Football profile</div>
        <div className="grid grid-cols-3 gap-3">
          <Field lbl="Playing position">
            <select value={f.playing_position} onChange={(e) => setF({ ...f, playing_position: e.target.value })}
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-me-position">
              <option value="">—</option>
              <option value="GK">Goalkeeper (GK)</option>
              <option value="CB">Centre Back (CB)</option>
              <option value="LB">Left Back (LB)</option>
              <option value="RB">Right Back (RB)</option>
              <option value="CDM">Defensive Mid (CDM)</option>
              <option value="CM">Centre Mid (CM)</option>
              <option value="CAM">Attacking Mid (CAM)</option>
              <option value="LW">Left Wing (LW)</option>
              <option value="RW">Right Wing (RW)</option>
              <option value="ST">Striker (ST)</option>
              <option value="CF">Centre Forward (CF)</option>
            </select>
          </Field>
          <Field lbl="Current form">
            <select value={f.form} onChange={(e) => setF({ ...f, form: e.target.value })}
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-me-form">
              <option value="">—</option>
              <option value="Excellent">🔥 Excellent</option>
              <option value="Good">✅ Good</option>
              <option value="Fair">➖ Fair</option>
              <option value="Poor">⚠️ Poor</option>
              <option value="Recovering">🩹 Recovering</option>
            </select>
          </Field>
          <Field lbl="Jersey #">
            <Input type="number" value={f.jersey_no} onChange={(v) => setF({ ...f, jersey_no: v })} testid="fc-me-jersey" />
          </Field>
        </div>
      </div>
      <Footer onCancel={onClose} onSave={save} saving={saving} testid="fc-me-save" />
    </Modal>
  );
}

function Dues() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const [memberMap, setMemberMap] = useState({});
  const load = async () => {
    const [d, m] = await Promise.all([
      api.get("/t/club/dues?status=pending"),
      api.get("/t/club/members"),
    ]);
    setItems(d.data || []);
    setMemberMap(Object.fromEntries((m.data || []).map((x) => [x.id, x])));
  };
  useEffect(() => { load(); }, []);
  const del = async (d) => {
    if (!window.confirm(`Delete due "${d.title}" for ${memberMap[d.member_id]?.full_name || "this member"}?`)) return;
    try { await api.delete(`/t/club/dues/${d.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  // Group by batch_id so we can show "× delete whole batch" for bulk-created dues
  const batches = {};
  for (const d of items) {
    if (d.batch_id) {
      if (!batches[d.batch_id]) batches[d.batch_id] = { id: d.batch_id, title: d.title, kind: d.kind, count: 0, total: 0 };
      batches[d.batch_id].count += 1;
      batches[d.batch_id].total += Number(d.amount || 0);
    }
  }
  const deleteBatch = async (b) => {
    if (!window.confirm(`Delete the entire bulk batch "${b.title}" (${b.count} dues, ${ngn(b.total)} total)?\nPaid dues will be skipped.`)) return;
    try {
      const r = await api.delete(`/t/club/dues/batch/${b.id}`);
      toast.success(`Deleted ${r.data.deleted} dues`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="fc-dues">
      <div className="flex items-center justify-end mb-4">
        <button onClick={() => setShow(true)} data-testid="fc-dues-bulk-btn" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> Bulk bill</button>
      </div>
      {Object.values(batches).length > 0 && (
        <div className="mb-4 bg-violet-50 border border-violet-200 rounded-xl p-3">
          <div className="text-[10px] uppercase font-bold tracking-wider text-violet-700 mb-2">Bulk batches</div>
          <div className="space-y-1.5">
            {Object.values(batches).map((b) => (
              <div key={b.id} className="flex items-center justify-between text-sm">
                <div className="truncate"><span className="font-bold">{b.title}</span> <span className="text-black/55 capitalize">· {(b.kind || "").replace("_", " ")} · {b.count} dues · {ngn(b.total)}</span></div>
                <button onClick={() => deleteBatch(b)} data-testid={`fc-dues-batch-del-${b.id}`}
                  className="text-[11px] font-bold text-rose-600 hover:bg-rose-100 px-2 py-1 rounded">Delete batch</button>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {items.length === 0 ? <Empty msg="No pending dues" /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Title</Th><Th>Member</Th><Th>Kind</Th><Th right>Amount</Th><Th>Due date</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-black/5" data-testid={`fc-dues-row-${d.id}`}>
                  <Td className="font-bold">{d.title}{d.period_label && <span className="ml-1.5 text-[9px] font-bold text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded uppercase">{d.period_label}</span>}</Td>
                  <Td className="text-xs">{memberMap[d.member_id]?.full_name || d.member_id?.slice(0, 8) + "…"}</Td>
                  <Td className="capitalize">{(d.kind || "").replace("_", " ")}</Td>
                  <Td right className="font-bold text-rose-700">{ngn(d.amount)}</Td>
                  <Td className="text-xs text-black/55">{(d.due_date || "").slice(0, 10)}</Td>
                  <Td>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setEdit(d)} data-testid={`fc-dues-edit-${d.id}`}
                        className="p-1 rounded hover:bg-black/[0.05] text-black/55"><Pencil size={12} /></button>
                      <button onClick={() => del(d)} data-testid={`fc-dues-delete-${d.id}`}
                        className="p-1 rounded hover:bg-rose-50 text-rose-600"><Trash2 size={12} /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {show && <BulkBillModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <DueEditModal due={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function DueEditModal({ due, onClose, onSaved }) {
  const [f, setF] = useState({ title: due.title || "", amount: due.amount, due_date: (due.due_date || "").slice(0, 10), kind: due.kind || "monthly_dues" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/t/club/dues/${due.id}`, { ...f, amount: parseFloat(f.amount) || 0 });
      toast.success("Updated"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Edit due" onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Title"><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="fc-due-edit-title" /></Field>
        <Field lbl="Amount (₦)"><Input type="number" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="fc-due-edit-amount" /></Field>
        <Field lbl="Due date"><Input type="date" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} /></Field>
        <Field lbl="Kind">
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
            <option value="monthly_dues">Monthly Dues</option>
            <option value="match_levy">Match Levy</option>
            <option value="special_levy">Special Levy</option>
            <option value="donation">Donation</option>
            <option value="competition">Competition Fee</option>
            <option value="registration_fee">Registration Fee</option>
          </select>
        </Field>
      </div>
      <Footer onCancel={onClose} onSave={save} saving={busy} testid="fc-due-edit-save" />
    </Modal>
  );
}

function BulkBillModal({ onClose, onSaved }) {
  const [f, setF] = useState({ kind: "monthly_dues", amount: 0, title: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.amount) return toast.error("Amount required");
    setBusy(true);
    try {
      const r = await api.post("/t/club/dues/bulk", { ...f, amount: parseFloat(f.amount) });
      if (r.data.created === 0) toast.success("No new dues — all members already billed for this period");
      else toast.success(`Billed ${r.data.created} active member(s)`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Bulk bill all active members" onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Type">
          <select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-bulk-kind">
            <option value="monthly_dues">Monthly Dues</option>
            <option value="match_levy">Match Levy</option>
            <option value="special_levy">Special Levy</option>
            <option value="donation">Donation</option>
            <option value="competition">Competition Fee</option>
          </select>
        </Field>
        <Field lbl="Amount per member (₦)"><Input type="number" value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="fc-bulk-amount" /></Field>
        <Field lbl="Title (optional)"><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} /></Field>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-bulk-save" saveLbl="Generate dues" />
    </Modal>
  );
}

function Payments() {
  const [tab, setTab] = useState("pending_approval");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/t/club/payments?status=${tab}`);
      setItems(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps
  const approve = async (pid) => {
    if (!window.confirm("Approve this payment? The matching due will be marked paid and the member will be activated if needed.")) return;
    setBusy(pid);
    try { await api.post(`/t/club/payments/${pid}/approve`, {}); toast.success("Payment approved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const reject = async (pid) => {
    const reason = window.prompt("Why are you rejecting this payment?", "");
    if (reason === null) return;
    setBusy(pid);
    try { await api.post(`/t/club/payments/${pid}/reject`, { reason }); toast.success("Payment rejected"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(""); }
  };
  const FILTERS = [
    { k: "pending_approval", lbl: "Pending approval", color: "bg-amber-100 text-amber-700" },
    { k: "approved", lbl: "Approved", color: "bg-emerald-100 text-emerald-700" },
    { k: "rejected", lbl: "Rejected", color: "bg-rose-100 text-rose-700" },
  ];
  return (
    <div data-testid="fc-payments">
      <div className="flex items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setTab(f.k)} data-testid={`fc-pay-filter-${f.k}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-md ${tab === f.k ? "bg-black text-white" : "bg-black/5 text-black/65 hover:bg-black/10"}`}>
            {f.lbl}
          </button>
        ))}
      </div>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {loading ? <Spinner /> : items.length === 0 ? <Empty msg={`No ${tab.replace("_", " ")} payments`} /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55">
              <tr><Th>Member</Th><Th>Kind</Th><Th right>Amount</Th><Th>Method</Th><Th>Reference</Th><Th>Submitted</Th><Th>Actions</Th></tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-black/5" data-testid={`fc-pay-row-${p.id}`}>
                  <Td className="font-bold">{p.member_name || p.member_id?.slice(0, 8)}</Td>
                  <Td className="capitalize">
                    {(p.kind || "").replace("_", " ")}
                    {Array.isArray(p.due_ids) && p.due_ids.length > 1 && (
                      <span className="ml-1.5 px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider align-middle">
                        ×{p.due_ids.length} periods
                      </span>
                    )}
                  </Td>
                  <Td right className="font-bold text-emerald-700">{ngn(p.amount)}</Td>
                  <Td className="capitalize text-xs text-black/65">{(p.method || "").replace("_", " ")}</Td>
                  <Td className="text-xs text-black/55 max-w-[180px] truncate" title={p.reference}>{p.reference || "—"}</Td>
                  <Td className="text-xs text-black/55">{(p.submitted_at || p.paid_at || p.created_at || "").slice(0, 16).replace("T", " ")}</Td>
                  <Td>
                    {tab === "pending_approval" ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => approve(p.id)} disabled={busy === p.id} data-testid={`fc-pay-approve-${p.id}`}
                          className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded disabled:opacity-50">Approve</button>
                        <button onClick={() => reject(p.id)} disabled={busy === p.id} data-testid={`fc-pay-reject-${p.id}`}
                          className="text-xs font-bold text-red-600 hover:bg-red-50 px-2 py-1 rounded disabled:opacity-50">Reject</button>
                      </div>
                    ) : tab === "rejected" ? (
                      <span className="text-[10px] uppercase font-bold text-rose-700" title={p.rejection_reason || ""}>{p.rejection_reason ? `Reason: ${p.rejection_reason}` : "Rejected"}</span>
                    ) : (
                      <span className="text-[10px] uppercase font-bold text-emerald-700">Approved</span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/t/club/settings").then((r) => setS(r.data || {})).catch(() => setS({})); }, []);
  if (!s) return <Spinner />;
  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/t/club/settings", s);
      toast.success("Saved — members will see the new fees & bank details on their next refresh");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="fc-settings">
      <div className="bg-white border border-black/8 rounded-2xl p-6">
        <h2 className="font-display font-extrabold text-lg mb-4">Club information</h2>
        <div className="space-y-3">
          <Field lbl="Club name"><Input value={s.club_name || ""} onChange={(v) => setS({ ...s, club_name: v })} testid="fc-set-name" /></Field>
          <Field lbl="Logo URL (Cloudinary)"><Input value={s.logo_url || ""} onChange={(v) => setS({ ...s, logo_url: v })} /></Field>
          <Field lbl="Address"><Input value={s.address || ""} onChange={(v) => setS({ ...s, address: v })} /></Field>
          <div className="grid grid-cols-2 gap-2">
            <Field lbl="State"><Input value={s.state || ""} onChange={(v) => setS({ ...s, state: v })} /></Field>
            <Field lbl="LGA"><Input value={s.lga || ""} onChange={(v) => setS({ ...s, lga: v })} /></Field>
          </div>
          <Field lbl="Website"><Input value={s.website || ""} onChange={(v) => setS({ ...s, website: v })} /></Field>
        </div>
      </div>
      <div className="bg-white border border-black/8 rounded-2xl p-6">
        <h2 className="font-display font-extrabold text-lg mb-4">Membership fees & dues</h2>
        <div className="space-y-3">
          <Field lbl="Registration fee (₦) — billed once on signup"><Input type="number" value={s.registration_fee || 0} onChange={(v) => setS({ ...s, registration_fee: parseFloat(v) || 0 })} testid="fc-set-regfee" /></Field>
          <Field lbl="Recurring dues amount (₦)"><Input type="number" value={s.monthly_dues || 0} onChange={(v) => setS({ ...s, monthly_dues: parseFloat(v) || 0 })} testid="fc-set-monthly" /></Field>
          <Field lbl="Dues frequency — how often each member is billed">
            <select value={s.dues_frequency || "monthly"} onChange={(e) => setS({ ...s, dues_frequency: e.target.value })} data-testid="fc-set-frequency"
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
              <option value="instant">Instant (one-time only — no recurring)</option>
            </select>
          </Field>
          <Field lbl="Match levy (₦)"><Input type="number" value={s.match_levy || 0} onChange={(v) => setS({ ...s, match_levy: parseFloat(v) || 0 })} /></Field>
          <Field lbl="Donation suggested (₦)"><Input type="number" value={s.donation_suggested || 0} onChange={(v) => setS({ ...s, donation_suggested: parseFloat(v) || 0 })} /></Field>
          <label className="flex items-center gap-2 text-sm cursor-pointer pt-2">
            <input type="checkbox" checked={!!s.auto_monthly_billing} onChange={(e) => setS({ ...s, auto_monthly_billing: e.target.checked })} className="accent-emerald-600" data-testid="fc-set-auto" />
            Auto-generate dues invoices at the chosen frequency
          </label>
        </div>
      </div>

      <div className="lg:col-span-2 bg-white border border-black/8 rounded-2xl p-6">
        <h2 className="font-display font-extrabold text-lg mb-1">Bank payment details</h2>
        <p className="text-xs text-black/55 mb-4">These will be shown to your members when they tap "Pay now" in their portal.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Field lbl="Bank name"><Input value={s.bank_name || ""} onChange={(v) => setS({ ...s, bank_name: v })} testid="fc-set-bank-name" /></Field>
          <Field lbl="Account number"><Input value={s.bank_account_number || ""} onChange={(v) => setS({ ...s, bank_account_number: v })} testid="fc-set-bank-acct" /></Field>
          <Field lbl="Account holder name"><Input value={s.bank_account_holder || ""} onChange={(v) => setS({ ...s, bank_account_holder: v })} testid="fc-set-bank-holder" /></Field>
        </div>
        <div className="mt-3">
          <Field lbl="Additional payment instructions (optional)">
            <textarea value={s.payment_instructions || ""} onChange={(e) => setS({ ...s, payment_instructions: e.target.value })}
              rows={3} placeholder="e.g. Please use your membership number as the transfer narration."
              className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-set-bank-instr" />
          </Field>
        </div>
      </div>

      <div className="lg:col-span-2">
        <button onClick={save} disabled={saving} data-testid="fc-settings-save"
          className="px-6 py-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">{saving ? "Saving…" : "Save settings"}</button>
      </div>
    </div>
  );
}

// shared
function KPI({ title, value, icon: Icon, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-black/8 p-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase font-bold text-black/55">{title}</div>
          <div className="mt-1 font-display font-extrabold text-2xl text-black capitalize">{value}</div>
        </div>
        {Icon && <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${accent || "bg-black/5 text-black/55"}`}><Icon size={18} /></div>}
      </div>
    </div>
  );
}
function Modal({ title, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-2xl shadow-2xl ${wide ? "max-w-3xl" : "max-w-xl"} w-full max-h-[92vh] overflow-y-auto`} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between sticky top-0 bg-white"><div className="font-display font-extrabold text-lg">{title}</div><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-black/5"><X size={16} /></button></div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}
function Field({ lbl, full, children }) { return <div className={full ? "col-span-2" : ""}><label className="text-[11px] uppercase font-bold text-black/55">{lbl}</label><div className="mt-1">{children}</div></div>; }
function Input({ value, onChange, type = "text", placeholder, testid }) { return <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid={testid} />; }
function Footer({ onCancel, onSave, saving, testid, saveLbl }) { return <div className="flex gap-2 pt-4 mt-4 border-t border-black/8"><button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button><button onClick={onSave} disabled={saving} className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50" data-testid={testid}>{saving ? "Saving…" : (saveLbl || "Save")}</button></div>; }
function Th({ children, right }) { return <th className={`px-4 py-2.5 ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right, className = "" }) { return <td className={`px-4 py-3 ${right ? "text-right" : ""} ${className}`}>{children}</td>; }
function Empty({ msg }) { return <div className="p-10 text-center text-sm text-black/45">{msg}</div>; }
function Spinner() { return <div className="p-12 flex items-center justify-center text-black/45"><Loader2 className="animate-spin" /></div>; }

// ─────────── LOANS ───────────
function Loans() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = () => api.get(`/t/club/loans?status=${filter}`).then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, [filter]);
  const approve = async (lid) => { try { await api.post(`/t/club/loans/${lid}/approve`, {}); toast.success("Approved — repayment schedule generated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const reject = async (lid) => { const reason = window.prompt("Rejection reason?"); if (reason === null) return; try { await api.post(`/t/club/loans/${lid}/reject`, { reason }); toast.success("Rejected"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const disburse = async (lid) => { if (!window.confirm("Disburse this loan? Member's repayment dues will be generated.")) return; try { await api.post(`/t/club/loans/${lid}/disburse`, {}); toast.success("Disbursed"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div data-testid="fc-loans">
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "disbursed", "repaid", "rejected"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`fc-loans-filter-${s}`}
            className={`px-3 py-1.5 text-xs font-bold rounded-md ${filter === s ? "bg-black text-white" : "bg-black/5 text-black/65"}`}>{s.toUpperCase()}</button>
        ))}
      </div>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {items.length === 0 ? <Empty msg={`No ${filter} loans`} /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55"><tr><Th>Member</Th><Th>Purpose</Th><Th right>Amount</Th><Th right>Term</Th><Th right>Balance</Th><Th>Actions</Th></tr></thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-t border-black/5" data-testid={`fc-loan-row-${l.id}`}>
                  <Td className="font-bold">{l.member_name}</Td>
                  <Td className="text-xs">{l.purpose}</Td>
                  <Td right className="font-bold">{ngn(l.approved_amount || l.amount)}</Td>
                  <Td right>{l.term_months}m</Td>
                  <Td right>{ngn(l.balance)}</Td>
                  <Td>
                    <div className="flex gap-1.5">
                      {l.status === "pending" && <><button onClick={() => approve(l.id)} data-testid={`fc-loan-approve-${l.id}`} className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-2 py-1 rounded">Approve</button><button onClick={() => reject(l.id)} className="text-xs font-bold text-red-600 hover:bg-red-50 px-2 py-1 rounded">Reject</button></>}
                      {l.status === "approved" && <button onClick={() => disburse(l.id)} data-testid={`fc-loan-disburse-${l.id}`} className="text-xs font-bold text-blue-700 hover:bg-blue-50 px-2 py-1 rounded">Disburse</button>}
                      {(l.status === "disbursed" || l.status === "repaid") && <span className="text-[10px] uppercase font-bold text-black/55">{l.status}</span>}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Teams() {
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [assignTo, setAssignTo] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/t/club/teams").then((r) => setTeams(r.data || []));
  useEffect(() => { load(); api.get("/t/club/members").then((r) => setMembers((r.data || []).filter((m) => m.status === "active"))).catch(() => {}); }, []);
  const reset = async (t) => {
    if (!window.confirm(`Reset "${t.name}"?\nThis clears the roster, points table and all SCHEDULED fixtures involving this team. Finished match history is preserved.`)) return;
    try {
      const r = await api.post(`/t/club/teams/${t.id}/reset`);
      toast.success(`Team reset · ${r.data.fixtures_deleted} fixtures removed`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? This cannot be undone.`)) return;
    try { await api.delete(`/t/club/teams/${t.id}`); toast.success("Team deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="fc-teams">
      <div className="flex justify-end mb-4"><button onClick={() => setShow(true)} data-testid="fc-team-new-btn" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> New Team</button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {teams.map((t) => (
          <div key={t.id} className="bg-white border border-black/8 rounded-2xl overflow-hidden" data-testid={`fc-team-${t.id}`}>
            <div className="h-3" style={{ background: t.color || "#1FA868" }} />
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl text-white flex items-center justify-center font-display font-extrabold text-xl overflow-hidden" style={{ background: t.color || "#1FA868" }}>
                  {t.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover" /> : (t.name || "?")[0]}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-extrabold text-base truncate">{t.name}</div>
                  <div className="text-xs text-black/55">{t.players?.length || 0} players</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-1 mt-3 text-center text-xs"><TeamStat lbl="P" v={t.stats?.P || 0} /><TeamStat lbl="W" v={t.stats?.W || 0} /><TeamStat lbl="D" v={t.stats?.D || 0} /><TeamStat lbl="L" v={t.stats?.L || 0} /></div>
              <div className="grid grid-cols-2 gap-1.5 mt-3">
                <button onClick={() => setAssignTo(t)} data-testid={`fc-team-assign-${t.id}`} className="col-span-2 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-xs font-bold">Assign players ({t.players?.length || 0})</button>
                <button onClick={() => setEdit(t)} data-testid={`fc-team-edit-${t.id}`} className="py-1.5 rounded-lg bg-black/[0.04] text-black/70 text-[11px] font-bold hover:bg-black/[0.08]">
                  <Pencil size={11} className="inline -mt-0.5 mr-1" /> Edit
                </button>
                <button onClick={() => reset(t)} data-testid={`fc-team-reset-${t.id}`} className="py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[11px] font-bold hover:bg-amber-100">
                  ↻ Reset
                </button>
                <button onClick={() => del(t)} data-testid={`fc-team-delete-${t.id}`} className="col-span-2 py-1.5 rounded-lg bg-rose-50 text-rose-700 text-[11px] font-bold hover:bg-rose-100">
                  <Trash2 size={11} className="inline -mt-0.5 mr-1" /> Delete team
                </button>
              </div>
            </div>
          </div>
        ))}
        {teams.length === 0 && <div className="col-span-full"><Empty msg="No teams yet" /></div>}
      </div>
      {show && <TeamEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} members={members} />}
      {edit && <TeamEditor team={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} members={members} />}
      {assignTo && <AssignPlayers team={assignTo} teams={teams} members={members} onClose={() => setAssignTo(null)} onSaved={() => { setAssignTo(null); load(); }} />}
    </div>
  );
}
function TeamStat({ lbl, v }) { return <div><div className="text-[9px] uppercase font-bold text-black/55">{lbl}</div><div className="font-display font-extrabold text-lg">{v}</div></div>; }

function TeamEditor({ team = null, onClose, onSaved, members }) {
  const isEdit = !!team;
  const [f, setF] = useState(team ? {
    name: team.name || "", color: team.color || "#1FA868",
    captain_member_id: team.captain_member_id || "", logo_url: team.logo_url || "",
  } : { name: "", color: "#1FA868", captain_member_id: "", logo_url: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name) return toast.error("Name required");
    setBusy(true);
    try {
      if (isEdit) await api.patch(`/t/club/teams/${team.id}`, f);
      else await api.post("/t/club/teams", f);
      toast.success(isEdit ? "Team updated" : "Team created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? `Edit ${team.name}` : "New team"} onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Team name"><Input value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="fc-team-name" /></Field>
        <Field lbl="Color"><input type="color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className="h-10 w-20 rounded-lg border border-black/15" /></Field>
        <Field lbl="Logo URL (Cloudinary)"><Input value={f.logo_url} onChange={(v) => setF({ ...f, logo_url: v })} /></Field>
        <Field lbl="Captain"><select value={f.captain_member_id} onChange={(e) => setF({ ...f, captain_member_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm"><option value="">Pick later</option>{members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}</select></Field>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-team-save" />
    </Modal>
  );
}

function AssignPlayers({ team, teams = [], members, onClose, onSaved }) {
  const [busy, setBusy] = useState(false);
  const currentIds = new Set((team.players || []).map((p) => p.member_id));
  // Map memberId → current team (excluding this one) for transfer-aware UI
  const memberTeam = {};
  for (const t of teams) {
    if (t.id === team.id) continue;
    for (const p of (t.players || [])) memberTeam[p.member_id] = t;
  }
  const assign = async (mid, isTransfer) => {
    if (isTransfer && !window.confirm("Transfer this player from their current team to " + team.name + "?\nTheir goals, assists & cards carry over.")) return;
    setBusy(true);
    try {
      const r = await api.post("/t/club/teams/assign-player", { team_id: team.id, member_id: mid, position: "MID" });
      toast.success(r.data?.transferred ? `Transferred from ${r.data.from_team.name}` : "Assigned");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Assign players → ${team.name}`} onClose={onClose} wide>
      <p className="text-xs text-black/55 mb-3">A player can only be in one team. Assigning a player from another team will transfer them — their stats are preserved.</p>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
        {members.map((m) => {
          const inTeam = currentIds.has(m.id);
          const otherTeam = memberTeam[m.id];
          return (
            <div key={m.id} className="flex items-center justify-between p-2.5 rounded-lg hover:bg-black/[0.02]" data-testid={`fc-assign-row-${m.id}`}>
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center font-bold overflow-hidden">
                  {m.photo_url ? <img src={m.photo_url} alt="" className="w-full h-full object-cover" /> : (m.full_name || "?")[0]}
                </div>
                <div>
                  <div className="font-bold text-sm">{m.full_name}</div>
                  <div className="text-xs text-black/55 flex items-center gap-1.5">
                    {m.email}
                    {otherTeam && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[9px] font-bold uppercase tracking-wider">
                        In: {otherTeam.name}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              {inTeam ? (
                <span className="text-[10px] font-bold uppercase text-emerald-700">In this team</span>
              ) : otherTeam ? (
                <button onClick={() => assign(m.id, true)} disabled={busy} data-testid={`fc-assign-transfer-${m.id}`}
                  className="text-xs font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded">
                  Transfer here
                </button>
              ) : (
                <button onClick={() => assign(m.id, false)} disabled={busy} data-testid={`fc-assign-${m.id}`}
                  className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded">
                  Assign
                </button>
              )}
            </div>
          );
        })}
        {members.length === 0 && <Empty msg="No active members yet" />}
      </div>
    </Modal>
  );
}

function Fixtures() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [resultFor, setResultFor] = useState(null);
  const load = () => api.get("/t/club/fixtures").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/t/club/teams").then((r) => setTeams(r.data || []));
    api.get("/t/club/members").then((r) => setMembers(r.data || []));
  }, []);
  const del = async (f) => {
    if (!window.confirm(`Delete fixture ${f.team_a_name} vs ${f.team_b_name}?${f.status === "finished" ? "\nThis will roll back team & player stats." : ""}`)) return;
    try { await api.delete(`/t/club/fixtures/${f.id}`); toast.success("Fixture deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="fc-fixtures">
      <div className="flex justify-end mb-4"><button onClick={() => setShow(true)} data-testid="fc-fix-new-btn" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> New Fixture</button></div>
      <div className="space-y-2">
        {items.map((f) => (
          <div key={f.id} className="bg-white border border-black/8 rounded-2xl p-4 flex items-center gap-4 flex-wrap" data-testid={`fc-fix-${f.id}`}>
            <div className="text-xs text-black/55 w-32">
              {(f.scheduled_at || "").slice(0, 16).replace("T", " ")}<br />{f.venue}
              {f.match_order && f.match_order > 0 && (
                <div className="mt-1 inline-block px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 text-[9px] font-bold uppercase tracking-wider">Match #{f.match_order}</div>
              )}
            </div>
            <div className="flex items-center gap-3 flex-1 min-w-[280px]">
              <div className="font-bold flex-1 text-right">{f.team_a_name}</div>
              {f.status === "finished" ? <div className="font-display font-extrabold text-2xl">{f.team_a_score} : {f.team_b_score}</div> : <div className="font-display font-extrabold text-xs text-black/55">vs</div>}
              <div className="font-bold flex-1">{f.team_b_name}</div>
            </div>
            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${f.status === "finished" ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>{f.status}</span>
            <div className="flex items-center gap-1">
              {f.status !== "finished" && (
                <button onClick={() => setResultFor(f)} data-testid={`fc-fix-result-${f.id}`}
                  className="text-xs font-bold text-emerald-700 hover:bg-emerald-50 px-3 py-1.5 rounded">
                  Enter result
                </button>
              )}
              <button onClick={() => setEditing(f)} data-testid={`fc-fix-edit-${f.id}`}
                className="p-1.5 rounded hover:bg-black/[0.05] text-black/55" title="Edit fixture">
                <Pencil size={13} />
              </button>
              <button onClick={() => del(f)} data-testid={`fc-fix-delete-${f.id}`}
                className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete fixture">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <Empty msg="No fixtures yet" />}
      </div>
      {show && <FixtureEditor teams={teams} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {editing && <FixtureEditor fixture={editing} teams={teams} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {resultFor && <ResultEntry fixture={resultFor} teams={teams} members={members} onClose={() => setResultFor(null)} onSaved={() => { setResultFor(null); load(); }} />}
    </div>
  );
}
function FixtureEditor({ fixture = null, teams, onClose, onSaved }) {
  const isEdit = !!fixture;
  const [f, setF] = useState(fixture ? {
    team_a_id: fixture.team_a_id, team_b_id: fixture.team_b_id,
    scheduled_at: (fixture.scheduled_at || "").slice(0, 16),
    venue: fixture.venue || "", competition: fixture.competition || "League",
    match_order: fixture.match_order || 1,
  } : { team_a_id: teams[0]?.id || "", team_b_id: teams[1]?.id || "", scheduled_at: "", venue: "", competition: "League", match_order: 1 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.team_a_id || !f.team_b_id || !f.scheduled_at) return toast.error("All fields required");
    setBusy(true);
    try {
      const payload = { ...f, match_order: parseInt(f.match_order) || 1 };
      if (isEdit) await api.patch(`/t/club/fixtures/${fixture.id}`, payload);
      else await api.post("/t/club/fixtures", payload);
      toast.success(isEdit ? "Fixture updated" : "Fixture scheduled");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? "Edit fixture" : "New fixture"} onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Team A"><select value={f.team_a_id} onChange={(e) => setF({ ...f, team_a_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        <Field lbl="Team B"><select value={f.team_b_id} onChange={(e) => setF({ ...f, team_b_id: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm">{teams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}</select></Field>
        <Field lbl="Scheduled at"><Input type="datetime-local" value={f.scheduled_at} onChange={(v) => setF({ ...f, scheduled_at: v })} testid="fc-fix-when" /></Field>
        <Field lbl="Match order on this day — 1st, 2nd, 3rd…">
          <select value={f.match_order} onChange={(e) => setF({ ...f, match_order: parseInt(e.target.value) })}
            className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-fix-order">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>{n === 1 ? "1st (opener)" : n === 2 ? "2nd" : n === 3 ? "3rd" : `${n}th`} match of the day</option>
            ))}
          </select>
        </Field>
        <Field lbl="Venue"><Input value={f.venue} onChange={(v) => setF({ ...f, venue: v })} /></Field>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-fix-save" />
    </Modal>
  );
}

function ResultEntry({ fixture, teams, members, onClose, onSaved }) {
  // Match-day stat lines: scorers, assisters, cards, attendees
  const [scoreA, setScoreA] = useState(0);
  const [scoreB, setScoreB] = useState(0);
  const [mvp, setMvp] = useState("");
  const [attendees, setAttendees] = useState([]);
  const [goals, setGoals] = useState([]);     // [{member_id, team_id, minute}]
  const [assists, setAssists] = useState([]);
  const [cards, setCards] = useState([]);      // [{member_id, team_id, kind: yellow|red, minute}]
  const [busy, setBusy] = useState(false);
  const teamA = teams.find((t) => t.id === fixture.team_a_id);
  const teamB = teams.find((t) => t.id === fixture.team_b_id);
  const playersA = teamA?.players || [];
  const playersB = teamB?.players || [];
  const allFixturePlayers = [...playersA.map((p) => ({ ...p, team_id: fixture.team_a_id, team_name: fixture.team_a_name })),
                              ...playersB.map((p) => ({ ...p, team_id: fixture.team_b_id, team_name: fixture.team_b_name }))];

  const addGoal = () => setGoals([...goals, { member_id: "", team_id: fixture.team_a_id, minute: "" }]);
  const addAssist = () => setAssists([...assists, { member_id: "", team_id: fixture.team_a_id, minute: "" }]);
  const addCard = () => setCards([...cards, { member_id: "", team_id: fixture.team_a_id, kind: "yellow", minute: "" }]);
  const toggleAttendance = (mid) => {
    const next = new Set(attendees);
    if (next.has(mid)) next.delete(mid); else next.add(mid);
    setAttendees([...next]);
  };
  const updateRow = (arr, setter, i, patch) => {
    const next = [...arr]; next[i] = { ...next[i], ...patch }; setter(next);
  };
  const submit = async () => {
    // Validate each stat row has a member
    const filledGoals = goals.filter((g) => g.member_id);
    const filledAssists = assists.filter((g) => g.member_id);
    const filledCards = cards.filter((g) => g.member_id);
    if (filledGoals.length !== goals.length) return toast.error("Pick a scorer for every goal row (or remove empty rows)");
    if (filledAssists.length !== assists.length) return toast.error("Pick an assister for every assist row");
    if (filledCards.length !== cards.length) return toast.error("Pick a player for every card row");
    setBusy(true);
    try {
      await api.post(`/t/club/fixtures/${fixture.id}/result`, {
        team_a_score: parseInt(scoreA) || 0,
        team_b_score: parseInt(scoreB) || 0,
        mvp_member_id: mvp || null,
        attendees,
        goals: filledGoals,
        assists: filledAssists,
        cards: filledCards,
      });
      toast.success("Result recorded · player stats updated");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`${fixture.team_a_name} vs ${fixture.team_b_name}`} onClose={onClose} wide>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <Field lbl={`${fixture.team_a_name} score`}><Input type="number" value={scoreA} onChange={(v) => setScoreA(v)} testid="fc-res-a" /></Field>
        <Field lbl={`${fixture.team_b_name} score`}><Input type="number" value={scoreB} onChange={(v) => setScoreB(v)} testid="fc-res-b" /></Field>
      </div>

      {/* Goals */}
      <StatBlock title="Goals" emoji="⚽" rows={goals} setRows={setGoals} onAdd={addGoal}
        render={(g, i) => (
          <>
            <PlayerSel value={g.member_id} onChange={(v) => updateRow(goals, setGoals, i, { member_id: v, team_id: allFixturePlayers.find((p) => p.member_id === v)?.team_id || g.team_id })} players={allFixturePlayers} testid={`fc-res-goal-${i}`} />
            <Input type="number" value={g.minute} onChange={(v) => updateRow(goals, setGoals, i, { minute: v })} placeholder="min" />
          </>
        )} />

      {/* Assists */}
      <StatBlock title="Assists" emoji="🎯" rows={assists} setRows={setAssists} onAdd={addAssist}
        render={(a, i) => (
          <>
            <PlayerSel value={a.member_id} onChange={(v) => updateRow(assists, setAssists, i, { member_id: v, team_id: allFixturePlayers.find((p) => p.member_id === v)?.team_id || a.team_id })} players={allFixturePlayers} testid={`fc-res-assist-${i}`} />
            <Input type="number" value={a.minute} onChange={(v) => updateRow(assists, setAssists, i, { minute: v })} placeholder="min" />
          </>
        )} />

      {/* Cards */}
      <StatBlock title="Cards" emoji="🟨🟥" rows={cards} setRows={setCards} onAdd={addCard}
        render={(c, i) => (
          <>
            <PlayerSel value={c.member_id} onChange={(v) => updateRow(cards, setCards, i, { member_id: v, team_id: allFixturePlayers.find((p) => p.member_id === v)?.team_id || c.team_id })} players={allFixturePlayers} testid={`fc-res-card-${i}`} />
            <select value={c.kind} onChange={(e) => updateRow(cards, setCards, i, { kind: e.target.value })} className="border border-black/15 rounded-lg px-2 py-2 text-sm">
              <option value="yellow">🟨 Yellow</option><option value="red">🟥 Red</option>
            </select>
            <Input type="number" value={c.minute} onChange={(v) => updateRow(cards, setCards, i, { minute: v })} placeholder="min" />
          </>
        )} />

      {/* Appearances */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] uppercase font-bold text-emerald-700 tracking-wider">👟 Appearances ({attendees.length})</div>
        </div>
        <div className="bg-[#FAFBFD] border border-black/8 rounded-xl p-3 grid grid-cols-2 md:grid-cols-3 gap-2 max-h-48 overflow-y-auto">
          {allFixturePlayers.map((p) => (
            <label key={p.member_id} className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={attendees.includes(p.member_id)} onChange={() => toggleAttendance(p.member_id)}
                className="accent-emerald-600" data-testid={`fc-res-att-${p.member_id}`} />
              <span className="truncate">{p.name}</span>
              <span className="text-[9px] font-bold text-black/45 ml-auto">{p.team_name?.slice(0, 6)}</span>
            </label>
          ))}
          {allFixturePlayers.length === 0 && <div className="col-span-full text-xs text-black/45 text-center py-3">No players assigned to these teams yet.</div>}
        </div>
      </div>

      <div className="mt-4"><Field lbl="MVP"><select value={mvp} onChange={(e) => setMvp(e.target.value)} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-res-mvp"><option value="">None</option>{allFixturePlayers.map((p) => <option key={p.member_id} value={p.member_id}>{p.name}</option>)}</select></Field></div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-res-save" saveLbl="Record result" />
    </Modal>
  );
}

function StatBlock({ title, emoji, rows, setRows, onAdd, render }) {
  const remove = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="mt-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[11px] uppercase font-bold text-emerald-700 tracking-wider">{emoji} {title} ({rows.length})</div>
        <button onClick={onAdd} className="text-[11px] font-bold text-emerald-700 hover:underline">+ Add</button>
      </div>
      <div className="space-y-1.5">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            {render(r, i)}
            <button onClick={() => remove(i)} className="text-rose-600 hover:bg-rose-50 p-1 rounded text-[11px] font-bold">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlayerSel({ value, onChange, players, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="flex-1 border border-black/15 rounded-lg px-2 py-2 text-sm">
      <option value="">Pick player…</option>
      {players.map((p) => <option key={p.member_id} value={p.member_id}>{p.name} ({p.team_name})</option>)}
    </select>
  );
}

function Executives() {
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/t/club/executives").then((r) => setItems(r.data || []));
  useEffect(() => { load(); api.get("/t/club/members").then((r) => setMembers((r.data || []).filter((m) => m.status === "active"))).catch(() => {}); }, []);
  const remove = async (e) => {
    if (!window.confirm(`Step down ${e.member_name} from ${(e.position || "").replace("_", " ")}? (Marks as 'former' — kept in history)`)) return;
    try { await api.delete(`/t/club/executives/${e.id}`); toast.success("Executive stepped down"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const hardDel = async (e) => {
    if (!window.confirm(`Permanently DELETE ${e.member_name}'s executive record? Cannot be undone.`)) return;
    try { await api.delete(`/t/club/executives/${e.id}/hard`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="fc-execs">
      <div className="flex justify-end mb-4"><button onClick={() => setShow(true)} data-testid="fc-exec-new-btn" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> Appoint executive</button></div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.filter((e) => e.status === "active").map((e) => (
          <div key={e.id} className="bg-white border border-black/8 rounded-2xl p-5 relative group" data-testid={`fc-exec-${e.id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center font-display font-extrabold text-xl overflow-hidden">
                {e.photo_url ? <img src={e.photo_url} alt={e.member_name} className="w-full h-full object-cover" /> : (e.member_name || "?")[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-extrabold text-base truncate">{e.member_name}</div>
                <div className="text-xs text-violet-700 font-bold capitalize">{(e.position || "").replace(/_/g, " ")}</div>
              </div>
            </div>
            <div className="text-xs text-black/55">Term: {(e.term_start || "").slice(0, 10)} → {(e.term_end || "—").slice(0, 10)}</div>
            <div className="mt-3 grid grid-cols-3 gap-1.5">
              <button onClick={() => setEdit(e)} data-testid={`fc-exec-edit-${e.id}`}
                className="py-1.5 rounded-lg bg-black/[0.04] text-black/70 text-[10px] font-bold hover:bg-black/[0.08]">
                <Pencil size={10} className="inline -mt-0.5 mr-1" />Edit
              </button>
              <button onClick={() => remove(e)} data-testid={`fc-exec-stepdown-${e.id}`}
                className="py-1.5 rounded-lg bg-amber-50 text-amber-700 text-[10px] font-bold hover:bg-amber-100">
                Step down
              </button>
              <button onClick={() => hardDel(e)} data-testid={`fc-exec-delete-${e.id}`}
                className="py-1.5 rounded-lg bg-rose-50 text-rose-700 text-[10px] font-bold hover:bg-rose-100">
                <Trash2 size={10} className="inline -mt-0.5 mr-1" />Delete
              </button>
            </div>
          </div>
        ))}
        {items.filter((e) => e.status === "active").length === 0 && <div className="col-span-full"><Empty msg="No executives appointed" /></div>}
      </div>
      {show && <ExecEditor members={members} onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <ExecEditor exec={edit} members={members} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function ExecEditor({ exec = null, members, onClose, onSaved }) {
  const isEdit = !!exec;
  const defaults = ["chairman", "vice_chairman", "secretary", "treasurer", "financial_secretary", "welfare_officer", "coach", "captain"];
  const [f, setF] = useState(exec ? {
    member_id: exec.member_id, position: exec.position,
    term_start: (exec.term_start || "").slice(0, 10),
    term_end: (exec.term_end || "").slice(0, 10),
  } : { member_id: "", position: "", term_start: new Date().toISOString().slice(0, 10), term_end: "" });
  const [busy, setBusy] = useState(false);
  const [positions, setPositions] = useState(defaults);
  const [showManager, setShowManager] = useState(false);
  useEffect(() => {
    api.get("/t/club/settings").then((r) => {
      const list = (r.data?.exec_positions || []);
      const safe = list.length > 0 ? list : defaults;
      setPositions(safe);
      setF((s) => ({ ...s, position: s.position || safe[0] }));
    }).catch(() => setF((s) => ({ ...s, position: s.position || defaults[0] })));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!f.member_id) return toast.error("Pick a member");
    if (!f.position) return toast.error("Pick a position");
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/t/club/executives/${exec.id}`, { position: f.position, term_start: f.term_start, term_end: f.term_end });
        toast.success("Updated");
      } else {
        await api.post("/t/club/executives", f);
        toast.success("Executive appointed");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? "Edit executive" : "Appoint executive"} onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Member">
          <select value={f.member_id} onChange={(e) => setF({ ...f, member_id: e.target.value })} disabled={isEdit}
            className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm disabled:bg-black/[0.04]" data-testid="fc-exec-member">
            <option value="">Pick member…</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.full_name}</option>)}
          </select>
        </Field>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] uppercase font-bold text-black/55">Position</label>
            <button type="button" onClick={() => setShowManager(true)} data-testid="fc-exec-manage-positions"
              className="text-[11px] font-bold text-emerald-700 hover:underline">+ Manage positions</button>
          </div>
          <select value={f.position} onChange={(e) => setF({ ...f, position: e.target.value })}
            className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" data-testid="fc-exec-position">
            {positions.map((p) => <option key={p} value={p}>{p.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field lbl="Term start"><Input type="date" value={f.term_start} onChange={(v) => setF({ ...f, term_start: v })} /></Field>
          <Field lbl="Term end"><Input type="date" value={f.term_end} onChange={(v) => setF({ ...f, term_end: v })} /></Field>
        </div>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-exec-save" />
      {showManager && (
        <ExecPositionsManager positions={positions}
          onClose={() => setShowManager(false)}
          onSaved={(next) => { setPositions(next); setShowManager(false); if (!next.includes(f.position)) setF((s) => ({ ...s, position: next[0] || "" })); }} />
      )}
    </Modal>
  );
}

function ExecPositionsManager({ positions, onClose, onSaved }) {
  const [list, setList] = useState(positions.length ? positions : [""]);
  const [busy, setBusy] = useState(false);
  const update = (i, v) => { const next = [...list]; next[i] = v; setList(next); };
  const remove = (i) => setList(list.filter((_, idx) => idx !== i));
  const add = () => setList([...list, ""]);
  const save = async () => {
    const clean = list.map((s) => (s || "").trim().toLowerCase().replace(/\s+/g, "_")).filter(Boolean);
    if (clean.length === 0) return toast.error("Add at least one position");
    setBusy(true);
    try {
      await api.patch("/t/club/settings", { exec_positions: clean });
      toast.success("Positions updated");
      onSaved(clean);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-black/8 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Executive positions</div>
            <div className="font-display font-extrabold text-lg">Manage positions</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X size={16} /></button>
        </div>
        <div className="p-6 space-y-2 max-h-[60vh] overflow-y-auto">
          {list.map((p, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`fc-exec-pos-row-${i}`}>
              <input value={p} onChange={(e) => update(i, e.target.value)}
                placeholder="e.g. assistant_coach"
                className="flex-1 border border-black/15 rounded-lg px-3 py-2 text-sm" />
              <button onClick={() => remove(i)} disabled={list.length <= 1}
                className="px-2 py-1 text-xs font-bold text-rose-600 hover:bg-rose-50 rounded disabled:opacity-30">
                Remove
              </button>
            </div>
          ))}
          <button onClick={add} data-testid="fc-exec-pos-add"
            className="w-full mt-2 py-2 rounded-lg border-2 border-dashed border-black/15 text-xs font-bold text-black/55 hover:border-emerald-400 hover:text-emerald-700">
            + Add another position
          </button>
          <p className="text-[11px] text-black/45 pt-2">Tip: use lowercase with underscores (e.g. <code className="font-mono">head_coach</code>). It will display as "head coach".</p>
        </div>
        <div className="flex gap-2 px-6 py-4 border-t border-black/8">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 rounded-lg bg-black/5 text-sm font-bold text-black/70">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="fc-exec-pos-save"
            className="flex-1 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold disabled:opacity-50">
            {busy ? "Saving…" : "Save positions"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Events() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/t/club/events").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);
  const toggle = async (e) => {
    try {
      const r = await api.post(`/t/club/events/${e.id}/toggle`);
      toast.success(r.data.status === "disabled" ? "Event disabled — hidden from members" : "Event re-enabled");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async (e) => {
    if (!window.confirm(`Delete event "${e.title}"? RSVPs and history will be lost.`)) return;
    try { await api.delete(`/t/club/events/${e.id}`); toast.success("Event deleted"); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  return (
    <div data-testid="fc-events">
      <div className="flex justify-end mb-4"><button onClick={() => setShow(true)} data-testid="fc-event-new-btn" className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-bold inline-flex items-center gap-2"><Plus size={14} /> New event</button></div>
      <div className="space-y-3">
        {items.map((e) => { const going = (e.rsvps || []).filter((r) => r.going).length; const disabled = (e.status || "active") === "disabled"; return (
          <div key={e.id} className={`bg-white border rounded-2xl p-5 ${disabled ? "border-zinc-200 opacity-60" : "border-black/8"}`} data-testid={`fc-event-${e.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">{e.kind}</span>
                  {disabled && <span className="text-[9px] uppercase font-bold tracking-wider bg-zinc-200 text-zinc-700 px-1.5 py-0.5 rounded">Hidden from members</span>}
                </div>
                <h3 className="font-display font-extrabold text-lg text-black mt-0.5">{e.title}</h3>
                <div className="text-xs text-black/55 mt-1">{(e.starts_at || "").slice(0, 16).replace("T", " ")} · {e.venue || "—"}</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right"><div className="text-[10px] uppercase font-bold text-black/55">Going</div><div className="font-display font-extrabold text-xl text-emerald-700">{going}</div></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setEdit(e)} data-testid={`fc-event-edit-${e.id}`}
                    className="p-1.5 rounded hover:bg-black/[0.05] text-black/55" title="Edit"><Pencil size={13} /></button>
                  <button onClick={() => toggle(e)} data-testid={`fc-event-toggle-${e.id}`}
                    className="p-1.5 rounded hover:bg-amber-50 text-amber-700" title={disabled ? "Re-enable" : "Disable"}>
                    {disabled ? <CheckCircle2 size={13} /> : <Ban size={13} />}
                  </button>
                  <button onClick={() => del(e)} data-testid={`fc-event-delete-${e.id}`}
                    className="p-1.5 rounded hover:bg-rose-50 text-rose-600" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
            {e.description && <p className="text-sm text-black/65 mt-2">{e.description}</p>}
          </div>
        ); })}
        {items.length === 0 && <Empty msg="No events scheduled" />}
      </div>
      {show && <EventEditor onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
      {edit && <EventEditor event={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
function EventEditor({ event = null, onClose, onSaved }) {
  const isEdit = !!event;
  const [f, setF] = useState(event ? {
    title: event.title || "", kind: event.kind || "meeting",
    starts_at: (event.starts_at || "").slice(0, 16),
    venue: event.venue || "", description: event.description || "", fee: event.fee || 0,
  } : { title: "", kind: "meeting", starts_at: "", venue: "", description: "", fee: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.title || !f.starts_at) return toast.error("Title & start time required");
    setBusy(true);
    try {
      const payload = { ...f, fee: parseFloat(f.fee) || 0 };
      if (isEdit) await api.patch(`/t/club/events/${event.id}`, payload);
      else await api.post("/t/club/events", payload);
      toast.success(isEdit ? "Event updated" : "Event created — all active members notified");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={isEdit ? "Edit event" : "New event"} onClose={onClose}>
      <div className="space-y-3">
        <Field lbl="Title"><Input value={f.title} onChange={(v) => setF({ ...f, title: v })} testid="fc-ev-title" /></Field>
        <Field lbl="Kind"><select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm"><option value="meeting">Meeting</option><option value="training">Training</option><option value="competition">Competition</option><option value="tournament">Tournament</option><option value="social">Social</option></select></Field>
        <Field lbl="Starts at"><Input type="datetime-local" value={f.starts_at} onChange={(v) => setF({ ...f, starts_at: v })} testid="fc-ev-when" /></Field>
        <Field lbl="Venue"><Input value={f.venue} onChange={(v) => setF({ ...f, venue: v })} /></Field>
        <Field lbl="Event fee (₦) — optional"><Input type="number" value={f.fee} onChange={(v) => setF({ ...f, fee: v })} /></Field>
        <Field lbl="Description"><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" /></Field>
      </div>
      <Footer onCancel={onClose} onSave={submit} saving={busy} testid="fc-ev-save" />
    </Modal>
  );
}

function Reminders() {
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/t/club/reminders/logs").then((r) => setLogs(r.data || []));
  useEffect(() => { load(); }, []);
  const send = async (channel) => {
    if (!window.confirm(`Send ${channel.toUpperCase()} reminders to all members with pending dues?`)) return;
    setBusy(true);
    try { const r = await api.post("/t/club/reminders/send", { channel }); toast.success(`Reminders queued for ${r.data.members_notified} member(s)`); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="fc-reminders">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        {["whatsapp", "email", "sms"].map((c) => (
          <button key={c} onClick={() => send(c)} disabled={busy} data-testid={`fc-rem-send-${c}`}
            className="bg-white border-2 border-emerald-200 hover:border-emerald-500 rounded-2xl p-5 text-left transition-all disabled:opacity-50">
            <Bell className="text-emerald-600 mb-2" size={20} />
            <div className="font-display font-extrabold text-base capitalize">{c} reminders</div>
            <div className="text-xs text-black/55 mt-1">Notify everyone with pending dues</div>
          </button>
        ))}
      </div>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base">Recent reminder logs</div>
        {logs.length === 0 ? <Empty msg="No reminders sent yet" /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55"><tr><Th>When</Th><Th>Channel</Th><Th>Member</Th><Th right>Due count</Th><Th right>Total</Th></tr></thead>
            <tbody>{logs.slice(0, 50).map((l) => (
              <tr key={l.id} className="border-t border-black/5">
                <Td className="text-xs text-black/55">{(l.created_at || "").slice(0, 16).replace("T", " ")}</Td>
                <Td className="capitalize">{l.channel}</Td>
                <Td className="text-xs">{l.member_id?.slice(0, 8)}…</Td>
                <Td right>{l.due_count}</Td>
                <Td right className="font-bold text-rose-700">{ngn(l.total_amount)}</Td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}

